import React, { useState, useEffect, useCallback } from 'react';
import {
  collection, query, orderBy, onSnapshot,
  addDoc, deleteDoc, doc, Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useTecnicos } from '../hooks/useTecnicos';
import FleetVehicleDetail from './FleetVehicleDetail';

// ── Helpers ────────────────────────────────────────────────────────────────────
function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr + 'T12:00:00') - new Date()) / 86400000);
}

function fmt(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

const STATUS_CFG = {
  activo: { label: 'Activo',     bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  taller: { label: 'En taller',  bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
  baja:   { label: 'Baja',       bg: 'bg-gray-100',  text: 'text-gray-500',  dot: 'bg-gray-400' },
};

function expiryStatus(dateStr) {
  const days = daysUntil(dateStr);
  if (days === null) return null;
  if (days < 0)  return { level: 'expired', label: `Vencida hace ${Math.abs(days)}d`, cls: 'text-red-600 font-semibold' };
  if (days <= 30) return { level: 'soon',   label: `Vence en ${days}d`,               cls: 'text-amber-600 font-semibold' };
  return               { level: 'ok',     label: fmt(dateStr),                       cls: 'text-green-600' };
}

const VEHICLE_INIT = {
  plate: '', brand: '', model: '', year: '', color: '', vin: '',
  assignedEmployeeId: '', status: 'activo', currentMileage: '', notes: '',
  insuranceCompany: '', insurancePolicyNumber: '', insuranceExpiryDate: '', insuranceAnnualCost: '',
  itvLastDate: '', itvNextDate: '', itvResult: '',
};

// ── Tarjeta de vehículo ────────────────────────────────────────────────────────
function VehicleCard({ vehicle, onSelect, tecnicos = [] }) {
  const status   = STATUS_CFG[vehicle.status] || STATUS_CFG.activo;
  const techName = tecnicos.find(t => String(t.employeeId) === String(vehicle.assignedEmployeeId))?.nombre;
  const itvSt    = expiryStatus(vehicle.itvNextDate);
  const segSt    = expiryStatus(vehicle.insuranceExpiryDate);

  return (
    <button
      onClick={() => onSelect(vehicle)}
      className="w-full bg-white rounded-xl border shadow-sm hover:shadow-md hover:border-gray-300 transition-all p-4 text-left"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10l1 1h1m8-1l1-1h3.5a.5.5 0 00.5-.5V9.621a1 1 0 00-.293-.707l-2.914-2.914A1 1 0 0015.086 6H13" />
          </svg>
          <span className="text-lg font-bold text-gray-900 tracking-wide">{vehicle.plate}</span>
        </div>
        <span className={`flex-shrink-0 text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1.5 ${status.bg} ${status.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
          {status.label}
        </span>
      </div>

      <p className="text-sm text-gray-600 mb-1">
        {vehicle.brand} {vehicle.model}{vehicle.year ? ` · ${vehicle.year}` : ''}
      </p>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-400 mb-3">
        {techName && <span>Técnico: <strong className="text-gray-600">{techName}</strong></span>}
        {vehicle.currentMileage && (
          <span>{Number(vehicle.currentMileage).toLocaleString('es-ES')} km</span>
        )}
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        {itvSt && (
          <span className={`flex items-center gap-1 ${itvSt.cls}`}>
            <span className="text-gray-400">ITV:</span> {itvSt.label}
          </span>
        )}
        {segSt && (
          <span className={`flex items-center gap-1 ${segSt.cls}`}>
            <span className="text-gray-400">Seguro:</span> {segSt.label}
          </span>
        )}
        {!itvSt && !segSt && (
          <span className="text-gray-300">Sin fechas registradas</span>
        )}
      </div>
    </button>
  );
}

// ── Vista principal ────────────────────────────────────────────────────────────
export default function FleetView() {
  const [vehicles, setVehicles]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showAddModal, setShowAddModal]   = useState(false);
  const [form, setForm]                   = useState(VEHICLE_INIT);
  const [saving, setSaving]               = useState(false);
  const [statusFilter, setStatusFilter]   = useState('all');
  const { tecnicos } = useTecnicos();

  useEffect(() => {
    const q = query(collection(db, 'fleet'), orderBy('plate'));
    const unsub = onSnapshot(q, snap => {
      setVehicles(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, err => { console.error(err); setLoading(false); });
    return unsub;
  }, []);

  // Refrescar el vehículo seleccionado cuando se actualice
  const handleVehicleUpdated = useCallback((updated) => {
    setVehicles(prev => prev.map(v => v.id === updated.id ? updated : v));
    setSelectedVehicle(updated);
  }, []);

  const handleAddVehicle = useCallback(async () => {
    if (!form.plate || !form.brand || !form.model) return;
    setSaving(true);
    try {
      await addDoc(collection(db, 'fleet'), {
        ...form,
        year:          form.year          ? parseInt(form.year)          : null,
        currentMileage: form.currentMileage ? parseInt(form.currentMileage) : null,
        insuranceAnnualCost: form.insuranceAnnualCost ? parseFloat(form.insuranceAnnualCost) : null,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      setForm(VEHICLE_INIT);
      setShowAddModal(false);
    } catch (e) { console.error(e); }
    setSaving(false);
  }, [form]);

  const handleDeleteVehicle = useCallback(async (v) => {
    if (!window.confirm(`¿Dar de baja el vehículo ${v.plate}? Esta acción no se puede deshacer.`)) return;
    await deleteDoc(doc(db, 'fleet', v.id));
    if (selectedVehicle?.id === v.id) setSelectedVehicle(null);
  }, [selectedVehicle]);

  // ── Alertas ──────────────────────────────────────────────────────────────────
  const alerts = vehicles.filter(v => {
    const itvD = daysUntil(v.itvNextDate);
    const segD = daysUntil(v.insuranceExpiryDate);
    return (itvD !== null && itvD <= 30) || (segD !== null && segD <= 30);
  });

  const filtered = statusFilter === 'all'
    ? vehicles
    : vehicles.filter(v => (v.status || 'activo') === statusFilter);

  // ── Detalle ──────────────────────────────────────────────────────────────────
  if (selectedVehicle) {
    return (
      <FleetVehicleDetail
        vehicle={selectedVehicle}
        onBack={() => setSelectedVehicle(null)}
        onVehicleUpdated={handleVehicleUpdated}
      />
    );
  }

  // ── Lista ─────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Flota</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {vehicles.length} vehículo{vehicles.length !== 1 ? 's' : ''} registrado{vehicles.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={() => { setForm(VEHICLE_INIT); setShowAddModal(true); }}
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 hover:bg-gray-700 text-white text-sm font-medium rounded-xl shadow-sm transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">Añadir vehículo</span>
            <span className="sm:hidden">Añadir</span>
          </button>
        </div>

        {/* Alertas */}
        {alerts.length > 0 && (
          <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-800 mb-1">
                  {alerts.length} vehículo{alerts.length !== 1 ? 's' : ''} con documentación próxima a vencer
                </p>
                <div className="space-y-1">
                  {alerts.map(v => {
                    const itvD = daysUntil(v.itvNextDate);
                    const segD = daysUntil(v.insuranceExpiryDate);
                    const issues = [];
                    if (itvD !== null && itvD <= 30) issues.push(`ITV ${itvD < 0 ? 'vencida' : `en ${itvD}d`}`);
                    if (segD !== null && segD <= 30) issues.push(`Seguro ${segD < 0 ? 'vencido' : `en ${segD}d`}`);
                    return (
                      <button key={v.id} onClick={() => setSelectedVehicle(v)}
                        className="block text-xs text-amber-700 hover:text-amber-900 underline text-left">
                        {v.plate} · {v.brand} {v.model} — {issues.join(', ')}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filtros de estado */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          {[
            { key: 'all', label: 'Todos' },
            { key: 'activo', label: 'Activos' },
            { key: 'taller', label: 'En taller' },
            { key: 'baja',   label: 'Baja' },
          ].map(f => (
            <button key={f.key} onClick={() => setStatusFilter(f.key)}
              className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === f.key
                  ? 'bg-gray-900 text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Lista */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400 gap-2">
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            Cargando flota…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10l1 1h1m8-1l1-1h3.5a.5.5 0 00.5-.5V9.621a1 1 0 00-.293-.707l-2.914-2.914A1 1 0 0015.086 6H13" />
            </svg>
            <p className="font-medium">
              {statusFilter === 'all' ? 'No hay vehículos registrados' : 'No hay vehículos en este estado'}
            </p>
            {statusFilter === 'all' && (
              <p className="text-sm mt-1">Añade el primer vehículo de tu flota</p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filtered.map(v => (
              <VehicleCard key={v.id} vehicle={v} onSelect={setSelectedVehicle} tecnicos={tecnicos} />
            ))}
          </div>
        )}
      </div>

      {/* ── Modal añadir vehículo ──────────────────────────────────────────────── */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setShowAddModal(false)}>
          <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="font-bold text-gray-900">Nuevo vehículo</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Datos básicos */}
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Datos del vehículo</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'plate', label: 'Matrícula *',      span: true },
                  { key: 'brand', label: 'Marca *' },
                  { key: 'model', label: 'Modelo *' },
                  { key: 'year',  label: 'Año', type: 'number' },
                  { key: 'color', label: 'Color' },
                  { key: 'vin',   label: 'Bastidor (VIN)', span: true },
                  { key: 'currentMileage', label: 'Km actuales', type: 'number' },
                ].map(f => (
                  <div key={f.key} className={f.span ? 'col-span-2' : ''}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{f.label}</label>
                    <input type={f.type || 'text'} value={form[f.key] || ''}
                      onChange={e => setForm(fm => ({ ...fm, [f.key]: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900" />
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Técnico asignado</label>
                  <select value={form.assignedEmployeeId || ''}
                    onChange={e => setForm(fm => ({ ...fm, assignedEmployeeId: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900">
                    <option value="">Sin asignar</option>
                    {tecnicos.map(t => <option key={t.id} value={t.employeeId}>{t.nombre}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Estado</label>
                  <select value={form.status}
                    onChange={e => setForm(fm => ({ ...fm, status: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900">
                    <option value="activo">Activo</option>
                    <option value="taller">En taller</option>
                    <option value="baja">Baja</option>
                  </select>
                </div>
              </div>

              {/* Seguro */}
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide pt-2">Seguro</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Aseguradora</label>
                  <input type="text" placeholder="Mapfre, Generali…" value={form.insuranceCompany}
                    onChange={e => setForm(fm => ({ ...fm, insuranceCompany: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nº póliza</label>
                  <input type="text" value={form.insurancePolicyNumber}
                    onChange={e => setForm(fm => ({ ...fm, insurancePolicyNumber: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Coste anual (€)</label>
                  <input type="number" min="0" step="0.01" value={form.insuranceAnnualCost}
                    onChange={e => setForm(fm => ({ ...fm, insuranceAnnualCost: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Vencimiento seguro</label>
                  <input type="date" value={form.insuranceExpiryDate}
                    onChange={e => setForm(fm => ({ ...fm, insuranceExpiryDate: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900" />
                </div>
              </div>

              {/* ITV */}
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide pt-2">ITV</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Última ITV</label>
                  <input type="date" value={form.itvLastDate}
                    onChange={e => setForm(fm => ({ ...fm, itvLastDate: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Próxima ITV</label>
                  <input type="date" value={form.itvNextDate}
                    onChange={e => setForm(fm => ({ ...fm, itvNextDate: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Resultado ITV</label>
                  <select value={form.itvResult}
                    onChange={e => setForm(fm => ({ ...fm, itvResult: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900">
                    <option value="">Sin resultado</option>
                    <option value="Favorable">Favorable</option>
                    <option value="Favorable con defectos leves">Favorable con defectos leves</option>
                    <option value="Desfavorable">Desfavorable</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notas</label>
                <textarea rows={2} value={form.notes}
                  onChange={e => setForm(fm => ({ ...fm, notes: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900 resize-none" />
              </div>
            </div>

            <div className="px-5 pb-5 flex gap-2 sticky bottom-0 bg-white border-t pt-4">
              <button onClick={() => setShowAddModal(false)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors">
                Cancelar
              </button>
              <button onClick={handleAddVehicle} disabled={saving || !form.plate || !form.brand || !form.model}
                className="flex-1 py-2.5 bg-gray-900 hover:bg-gray-700 disabled:opacity-50 text-white text-sm font-medium rounded-xl transition-colors">
                {saving ? 'Guardando…' : 'Añadir vehículo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
